import { Trans, useLingui } from "@lingui/react/macro";
import type {
  AgentSkillCatalogEntry,
  Bot,
  Group,
  Me,
  SpaceBot,
  SpaceMemoryConfig,
  ThreadMessage,
  VoiceStatus,
} from "@rakazo/contracts";
import {
  answerableAskMessageIds,
  channelMessageSender,
  chatUserBubbleColors,
  dismissableAskMessageIds,
  foldPeerReplies,
  groupBotsForSidebar,
  isActive,
  isRunWorking,
  memberName,
} from "@rakazo/core";
import { fetchFresh, writeSaved } from "@rakazo/core/query";
import type { GroupAvatarMember } from "@rakazo/ui-web";
import { BrandMark, Button } from "@rakazo/ui-web";
import { useIsMobile } from "@rakazo/ui-web/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { Monitor, Moon, Plug, ScrollText, Sun, Users } from "lucide-react";
import type { ReactNode } from "react";
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useParams } from "react-router-dom";
import { ComputerUpdateProgress } from "../components/ComputerUpdateProgress";
import { CallCard, CallReturnBar } from "../components/call/CallCard";
import { IncomingCall } from "../components/call/IncomingCall";
import { PageLoading } from "../components/PageLoading";
import type { ArtifactTarget } from "../lib/artifact-open";
import { authClient } from "../lib/auth";
import { peekBootstrapSeed } from "../lib/bootstrap";
import type { BotsSidebarDensity } from "../lib/bots-sidebar-density-pref";
import {
  getBotsSidebarDensity,
  subscribeBotsSidebarDensity,
} from "../lib/bots-sidebar-density-pref";
import { readBotsSidebarCollapsed, writeBotsSidebarCollapsed } from "../lib/bots-sidebar-pref";
import { setCallMinimized, startCall, useCallSession } from "../lib/call-session";
import {
  getComposerEnterToSend,
  subscribeComposerEnterToSend,
} from "../lib/composer-enter-to-send-pref";
import { computersAreUnavailable } from "../lib/computer-sandbox";
import { desktopBridge } from "../lib/desktop";
import { hasOpenDialog } from "../lib/has-open-dialog";
import { markAfterPaint, markOnce } from "../lib/performance";
import { usePreloadRecoveryState } from "../lib/preload-recovery";
import { lastChatKey, lastChatOf, queryClient, spaceQueries, spaceSeedKey } from "../lib/query";
import type { Panel } from "../lib/right-panel-state";
import { rightPanelStorageKey } from "../lib/right-panel-state";
import { clearSpaceSelection, createSpaceRpc, rpc, selectedSpaceId, selectSpace } from "../lib/rpc";
import { markShellReady, useSpaceEpoch } from "../lib/space-switch";
import type { TeamTemplate } from "../lib/team-templates";
import { isTeamBot, isTeamLeader } from "../lib/teams";
import { activeThreadRuns } from "../lib/thread-events";
import { getToolActivityEnabled, subscribeToolActivity } from "../lib/tool-activity-preference";
import type { AppearancePreference } from "../lib/ui-appearance";
import { getUiAppearancePreference, setUiAppearance } from "../lib/ui-appearance";
import { useDesktopBadge } from "../lib/use-desktop-badge";
import { HostComputerPrompt } from "./HostComputerPrompt";
import { RoutineList } from "./RoutineEditor";
import { useSettingsNavItems } from "./settings-nav";
import { ArchivedChats } from "./shell/archived-chats";
import { ChatContextMenu } from "./shell/chat-context-menu";
import { ChatHeader } from "./shell/chat-header";
import { FALLBACK_BOT_COLOR } from "./shell/chat-shared";
import type { CommandPaletteAction } from "./shell/command-palette";
import { CompanyWelcome } from "./shell/company-welcome";
import { Composer } from "./shell/composer";
import { ComputerOverlay } from "./shell/computer-overlay";
import { ComputerPanel } from "./shell/computer-panel";
import { ShellDialogs, useShellDialogs } from "./shell/shell-dialogs";
import { AppOverlays, SettingsOverlays } from "./shell/shell-overlays";
import { cycleBotId, useShellShortcuts } from "./shell/shell-shortcuts";
import { focusSidebarSearch, ShellSidebar } from "./shell/shell-sidebar";
import { ShellSidePanel } from "./shell/side-panel";
import { BotsSidebarLayout } from "./shell/sidebar-layout";
import { SidebarRoster } from "./shell/sidebar-roster";
import { commitSpaceRename, countUnreadChats, sidebarGroupsForSpaces } from "./shell/space-sidebar";
import { Transcript } from "./shell/transcript";
import { useBrowserNotifications } from "./shell/use-browser-notifications";
import { useChatRead } from "./shell/use-chat-read";
import { useComposerActions } from "./shell/use-composer-actions";
import { useComposerMentions } from "./shell/use-composer-mentions";
import { useComputerControls, useComputerState } from "./shell/use-computer";
import { useMessageJump } from "./shell/use-message-jump";
import { useMobileSidebar } from "./shell/use-mobile-sidebar";
import { useReadAloud } from "./shell/use-read-aloud";
import { useRightPanel, useRightPanelRestore } from "./shell/use-right-panel";
import { useRoutineEditor } from "./shell/use-routine-editor";
import { firstThreadRoute, useShellNavigation } from "./shell/use-shell-navigation";
import { useSidebarExpansion } from "./shell/use-sidebar-expansion";
import { useSidebarSearch } from "./shell/use-sidebar-search";
import { useRosterDrop, useSpaceBootstrap, useSpaceRoster } from "./shell/use-space-roster";
import { useOpenSpaceChat } from "./shell/use-space-switch";
import { useThreadState } from "./shell/use-thread-state";
import { useThreadSync } from "./shell/use-thread-sync";
import type { SettingsSection } from "./shell-overlay";

const NO_AGENT_SKILLS: AgentSkillCatalogEntry[] = [];
const NO_PROVIDERS: string[] = [];

export function ShellPage() {
  // A new key per space switch, so nothing from the previous space survives it.
  const spaceEpoch = useSpaceEpoch();
  return (
    <CompanyGate>
      {(noCompany) => <SpaceShell key={spaceEpoch} noCompany={noCompany} />}
    </CompanyGate>
  );
}

type CompanyGateState =
  | { kind: "loading" }
  | { kind: "create-company"; me: Me }
  | { kind: "empty" }
  | { kind: "ready" }
  | { kind: "error" };

/** Shows company creation before model setup, or the empty shell when a model is ready. */
function CompanyGate({ children }: { children: (noCompany: boolean) => ReactNode }) {
  const { botId, groupId } = useParams();
  const [state, setState] = useState<CompanyGateState>({ kind: "loading" });
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        let navigation = await rpc.spaces.list();
        // A saved Space the server no longer knows leaves no actor with that header.
        if (navigation.spaces.length === 0 && selectedSpaceId()) {
          clearSpaceSelection();
          navigation = await rpc.spaces.list();
        }
        const me = await rpc.me();
        if (!active) return;
        const companies = navigation.spaces.filter((space) => space.companyPlatform);
        if (companies.length === 0) {
          clearSpaceSelection();
          setState(me.needsModel ? { kind: "create-company", me } : { kind: "empty" });
          return;
        }
        const selected = selectedSpaceId();
        const company =
          companies.find((space) =>
            groupId
              ? space.groups.some((group) => group.id === groupId)
              : botId
                ? space.bots.some((bot) => bot.id === botId)
                : false,
          ) ??
          companies.find((space) => space.id === selected) ??
          companies[0]!;
        if (company.id !== selected) selectSpace(company.id);
        setState({ kind: "ready" });
      } catch {
        if (active) setState({ kind: "error" });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (state.kind === "create-company")
    return <CompanyWelcome me={state.me} onCreated={() => setState({ kind: "ready" })} />;
  if (state.kind === "loading") return <PageLoading />;
  return children(state.kind === "empty");
}

function SpaceShell({ noCompany = false }: { noCompany?: boolean }) {
  const { t } = useLingui();
  const { botId, groupId } = useParams();
  const [shellSpaceId] = useState(selectedSpaceId);
  const [rpc] = useState(() => createSpaceRpc(shellSpaceId));
  const session = authClient.useSession();
  const userId = session.data?.user.id;
  // A revisited space, or the shell reopened from another page, starts from what it showed
  // last time while its fresh bootstrap loads.
  const [{ seed, startChat }] = useState(() => {
    const primed = peekBootstrapSeed(botId);
    if (primed) return { seed: primed, startChat: undefined };
    const cached = queryClient.getQueryData(spaceSeedKey(userId ?? null, shellSpaceId));
    const lastChat = queryClient.getQueryData(lastChatKey(userId ?? null, shellSpaceId));
    return { seed: cached, startChat: botId || groupId ? undefined : lastChatOf(cached, lastChat) };
  });
  const {
    navigate,
    navigationRevision,
    overlayRevision,
    alive,
    searchParams,
    setSearchParams,
    searchParamsRef,
    redirectKeepingOverlay,
    overlay,
    setOverlay,
  } = useShellNavigation();
  const queries = spaceQueries(userId ?? null, shellSpaceId);
  const roster = useSpaceRoster({
    rpc,
    seed,
    botId,
    groupId,
    navigate,
    redirectKeepingOverlay,
    noCompany,
  });
  const {
    groups,
    setGroups,
    bots,
    setBots,
    botsRef,
    botSections,
    spaces,
    setSpaces,
    archivedBots,
    archivedGroups,
    initialBotsLoaded,
    initialLoadFailed,
    bootstrapMe,
    setBootstrapMe,
    refreshBots,
  } = roster;
  const { collapsedSidebarSections, toggleSidebarSection } = useSidebarExpansion(userId);
  const [botsSidebarCollapsed, setBotsSidebarCollapsed] = useState(false);
  useEffect(() => {
    setBotsSidebarCollapsed(readBotsSidebarCollapsed(userId));
  }, [userId]);
  const search = useSidebarSearch({ rpc, navigate });
  const thread = useThreadState();
  const { snapshot } = thread;
  const showToolActivity = useSyncExternalStore(
    subscribeToolActivity,
    getToolActivityEnabled,
    () => true,
  );
  const botsSidebarDensity = useSyncExternalStore<BotsSidebarDensity>(
    subscribeBotsSidebarDensity,
    getBotsSidebarDensity,
    () => "comfortable",
  );
  const enterToSend = useSyncExternalStore(
    subscribeComposerEnterToSend,
    getComposerEnterToSend,
    () => false,
  );
  const rightPanel = useRightPanel({ searchParams, setSearchParams });
  const { panel, setPanel, modelFocusRequest, setModelFocusRequest } = rightPanel;
  const lastBotTab = useRef<Panel>("routines");
  if (panel === "routines" || panel === "computer" || panel === "settings")
    lastBotTab.current = panel;
  const [peerConversation, setPeerConversation] = usePreloadRecoveryState<{
    peerBotId: string;
    peerBotName: string;
  } | null>("peerConversation", null);
  const agentSkillsQuery = useQuery(queries.agentSkills.list.queryOptions());
  const agentSkills = agentSkillsQuery.data ?? NO_AGENT_SKILLS;
  const setAgentSkills = useCallback(
    (skills: AgentSkillCatalogEntry[]) =>
      void writeSaved(queryClient, queries.agentSkills.list.queryKey(), () => skills),
    [queries],
  );
  const refreshAgentSkills = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queries.agentSkills.list.queryKey() });
  }, [queries]);
  const [teachBusy, setTeachBusy] = useState(false);
  const messagingStatus = useQuery({
    ...queries.messaging.status.queryOptions(),
    enabled: Boolean(session.data?.user),
  }).data;
  const messagingSurfaceEnabled = messagingStatus?.enabled ?? false;
  const messagingProviders = messagingStatus?.providers ?? NO_PROVIDERS;
  const memoryConfigQuery = useQuery(queries.memory.providerConfig.queryOptions());
  // Undefined while it first loads; null when the space has none or it could not be read.
  const memoryProviderConfig: SpaceMemoryConfig | null | undefined =
    memoryConfigQuery.data !== undefined
      ? memoryConfigQuery.data
      : memoryConfigQuery.isError
        ? null
        : undefined;
  const call = useCallSession();
  /** Calls a bot, or opens voice settings first when no voice is connected. */
  const callBot = (bot: Bot) => {
    if (!bootstrapMe?.phoneCallsEnabled) return;
    if (!voiceStatus?.ready) {
      openSettings("voice");
      return;
    }
    startCall({
      botId: bot.id,
      botName: bot.name,
      botColor: bot.color,
      botAvatarSet: bot.avatarSet,
      transcribe: voiceStatus ? Boolean(voiceStatus.transcribe) : undefined,
    });
  };
  const voiceStatus: VoiceStatus | null =
    useQuery(queries.voice.status.queryOptions()).data ?? null;
  const { mobileSidebarOpen, setMobileSidebarOpen, swipeHandlers } = useMobileSidebar();
  const isMobileSidebar = useIsMobile();
  const miniSidebar = botsSidebarCollapsed && !isMobileSidebar;
  // A panel remembered from the desktop would cover a phone's chat on arrival.
  useEffect(() => {
    if (isMobileSidebar) setPanel(null);
  }, [isMobileSidebar, setPanel]);
  const [botMenu, setBotMenu] = useState<{ kind: "bot" | "group"; id: string } | null>(null);
  const closeBotMenu = useCallback(() => setBotMenu(null), []);
  const currentSpaceId = selectedSpaceId() ?? bootstrapMe?.spaceId ?? null;
  // null: settings follow the space the app is showing.
  const settingsSpaceId = overlay?.kind === "settings" ? overlay.spaceId : null;
  const settingsSpace =
    spaces.find((space) => space.id === (settingsSpaceId ?? currentSpaceId)) ?? null;
  // Never fall back to another space's settings when the edited space goes away.
  const settingsSpaceGone = initialBotsLoaded && settingsSpaceId !== null && !settingsSpace;
  useEffect(() => {
    if (settingsSpaceGone) setOverlay(null);
  }, [settingsSpaceGone]);
  // The app menu owns Cmd+, because macOS takes it before the page sees a keydown.
  const openSettingsRef = useRef(openSettings);
  openSettingsRef.current = openSettings;
  useEffect(() => desktopBridge()?.settings?.onOpen(() => openSettingsRef.current("general")), []);

  const inGroup = Boolean(groupId);
  const chatPanelOpen = inGroup
    ? panel === "group-settings"
    : panel === "computer" || panel === "routines" || panel === "settings" || panel === "routine";
  // Until the route names the chat reopened from cache, that chat stands in for it.
  const routeChat = botId || groupId ? undefined : startChat;
  const active =
    inGroup || routeChat?.groupId
      ? undefined
      : (bots.find((b) => b.id === (botId ?? routeChat?.botId)) ?? bots[0]);
  const selectBot = useCallback(
    (id: string) => {
      setMobileSidebarOpen(false);
      navigate(`/app/${id}`);
    },
    [navigate, setMobileSidebarOpen],
  );
  const userBubble = useMemo(
    () => (active ? chatUserBubbleColors(active.color, active.id) : undefined),
    [active?.color, active?.id],
  );
  const activeGroup = groups.find((group) => group.id === groupId);
  const panelPermanent = !isMobileSidebar && Boolean(active || activeGroup);
  // The right panel is permanent now, so a tab from the previous chat must not survive the switch:
  // a group only has its company panel, and a bot cannot keep the group tab.
  useEffect(() => {
    // A phone shows one surface at a time: the panel opens on demand instead.
    if (!panelPermanent) return;
    if (inGroup) {
      if (panel !== "group-settings") setPanel("group-settings");
    } else if (panel === "group-settings") {
      setPanel("settings");
    }
  }, [inGroup, panel, panelPermanent, setPanel]);
  // The leader is the one team member whose spawnKey marks it, so the group can offer a private chat.
  const activeGroupLeader = useMemo(() => {
    if (!activeGroup) return undefined;
    return activeGroup.members
      .map((member) => bots.find((bot) => bot.id === member.botId))
      .find((bot) => isTeamLeader(bot?.spawnKey));
  }, [activeGroup, bots]);
  // Private member chats stay nested even for companies in other Spaces.
  const teamDms = useMemo(() => {
    const byGroup = new Map<string, SpaceBot[]>();
    const lists = spaces.length
      ? spaces.map((space) => (space.id === bootstrapMe?.spaceId ? { groups, bots } : space))
      : [{ groups, bots }];
    for (const { groups: spaceGroups, bots: spaceBots } of lists) {
      for (const group of spaceGroups) {
        const members = group.members
          .flatMap((member) => {
            const bot = spaceBots.find((item) => item.id === member.botId);
            return bot && isTeamBot(bot) && (bot.preview || bot.unread || bot.id === active?.id)
              ? [bot]
              : [];
          })
          .sort(
            (left, right) =>
              Number(isTeamLeader(right.spawnKey)) - Number(isTeamLeader(left.spawnKey)) ||
              right.updatedAt.localeCompare(left.updatedAt),
          );
        if (members.length > 0) byGroup.set(group.id, members);
      }
    }
    return byGroup;
  }, [active?.id, bootstrapMe?.spaceId, bots, groups, spaces]);
  const activeBotId = useRef<string | undefined>(inGroup ? undefined : active?.id);
  activeBotId.current = inGroup ? undefined : active?.id;
  const activeGroupId = useRef<string | undefined>(groupId);
  activeGroupId.current = groupId;
  const computerState = useComputerState({ rpc, panel, activeId: active?.id, activeBotId });
  const { computer, computerOpen, setComputerOpen, booting, commitComputer } = computerState;
  const cycleBots = useMemo(
    () => groupBotsForSidebar(bots, botSections).flatMap((group) => group.bots),
    [bots, botSections],
  );
  const dialogs = useShellDialogs();
  useShellShortcuts({
    run: (shortcut) => {
      switch (shortcut.type) {
        case "command-menu":
          dialogs.setCommandPaletteOpen((open) => !open);
          return true;
        case "new":
          dialogs.setCreateTeamOpen(true);
          return true;
        case "back":
        case "forward":
          void navigate(shortcut.type === "back" ? -1 : 1);
          return true;
      }
      // The full-screen computer covers the sidebar and takes the keyboard.
      if (computerOpen) return false;
      if (shortcut.type === "toggle-sidebar") {
        if (isMobileSidebar) setMobileSidebarOpen((open) => !open);
        else setBotsSidebarCollapsedPref(!botsSidebarCollapsed);
        return true;
      }
      if (shortcut.type === "search") {
        if (isMobileSidebar) setMobileSidebarOpen(true);
        else if (botsSidebarCollapsed) setBotsSidebarCollapsedPref(false);
        if (!search.activityMode) search.setSearchOpen(true);
        focusSidebarSearch();
        return true;
      }
      // The command menu picks its own Cmd/Ctrl+1–9 from what it lists.
      if (hasOpenDialog(document, true)) return false;
      const botId =
        shortcut.type === "bot"
          ? cycleBots[shortcut.index]?.id
          : cycleBotId(cycleBots, active?.id, shortcut.type === "next-bot" ? 1 : -1);
      if (!botId) return false;
      selectBot(botId);
      return true;
    },
    onTyping: () => {
      if (computerOpen || hasOpenDialog(document, true)) return;
      document
        .querySelector<HTMLTextAreaElement>('textarea[name="chat-message"]:not(:disabled)')
        ?.focus();
    },
  });
  useSpaceBootstrap({
    rpc,
    roster,
    thread,
    commitComputer,
    userId,
    shellSpaceId,
    seed,
    startChat,
    botId,
    groupId,
    chat: groupId ? { groupId } : active ? { botId: active.id } : undefined,
    navigate,
    redirectKeepingOverlay,
    noCompany,
  });
  const { notifyBrowserForEvent, flushPendingBrowserNotifications } =
    useBrowserNotifications(botsRef);
  const { speakingMessageId, speakMessage } = useReadAloud({
    active,
    snapshot,
    callBotId: call?.botId,
    activeBotId,
  });
  const read = useChatRead({ rpc, active, groupId, activeGroup, setBots, setGroups });
  const {
    refreshThread,
    refreshGroupThread,
    refreshThreadRef,
    refreshGroupThreadRef,
    refreshActiveThread,
    loadOlder,
    loadingOlder,
    terminalRunReceipts,
  } = useThreadSync({
    rpc,
    queries,
    thread,
    computerState,
    seed,
    active,
    inGroup,
    groupId,
    activeGroup,
    activeBotId,
    activeGroupId,
    botsRef,
    searchParamsRef,
    refreshBots,
    notifyBrowserForEvent,
    markBotReadIfVisible: read.markBotReadIfVisible,
    markGroupReadOnReply: read.markGroupReadOnReply,
  });
  const {
    editorProps: routineEditorProps,
    deleteDialogProps: deleteRoutineDialogProps,
    editingRoutine,
    restoreRoutine,
    editRoutine,
    startRoutine,
    resetRoutineTarget,
    leaveRoutine,
    addSkillRoutine,
  } = useRoutineEditor({
    rpc,
    active,
    activeBotId,
    timezone: bootstrapMe?.timezone,
    setBots,
    refreshThread,
    setPanel,
    messageProviders: messagingProviders,
  });
  // Until this visit's read lands, a bot shows the routines and skills it had last time.
  const activeRoutines =
    inGroup || !active
      ? []
      : thread.routinesBotId === active.id
        ? thread.routines
        : (queryClient.getQueryData(
            queries.routines.list.queryKey({ input: { botId: active.id } }),
          ) ?? []);
  const activeTaughtSkills = !active
    ? []
    : thread.taughtSkillsBotId === active.id
      ? thread.taughtSkills
      : (queryClient.getQueryData(queries.skills.list.queryKey({ input: { botId: active.id } })) ??
        []);
  const recordingSkill = activeTaughtSkills.find((skill) => skill.status === "recording") ?? null;

  // The roster lists teams, plus a private chat row once that chat exists (or is open).
  // A bot is never a row by itself: team members sit under their team, other bots appear
  // only through a conversation the user actually started.
  const sidebarBots = useMemo(() => {
    const hasConversation = (bot: { id: string; preview: string; unread: boolean }) =>
      Boolean(bot.preview || bot.unread) || bot.id === active?.id;
    return bots.filter((bot) => !isTeamBot(bot) && hasConversation(bot));
  }, [active?.id, bots]);
  const sidebarSpaces = useMemo(
    () =>
      noCompany
        ? []
        : spaces
            .filter((space) => space.companyPlatform)
            .map((space) =>
              space.id === bootstrapMe?.spaceId
                ? {
                    ...space,
                    bots: sidebarBots,
                    groups,
                    botSections,
                  }
                : {
                    ...space,
                    bots: space.bots.filter(
                      (bot) => !isTeamBot(bot) && (bot.preview || bot.unread),
                    ),
                  },
            ),
    [bootstrapMe, botSections, groups, sidebarBots, spaces, noCompany],
  );
  const sidebarGroups = useMemo(
    () =>
      sidebarGroupsForSpaces(sidebarSpaces, miniSidebar ? "" : search.query, {
        pinned: t`Pinned`,
        unassigned: t`Unassigned`,
      }),
    [sidebarSpaces, search.query, miniSidebar, t],
  );
  // The Dock badge ignores the roster search box: a filter must not hide unread chats.
  const unreadChatCount = useMemo(
    () => countUnreadChats(sidebarGroupsForSpaces(sidebarSpaces, "")),
    [sidebarSpaces],
  );
  useDesktopBadge(userId ? unreadChatCount : 0);
  const openSpaceChat = useOpenSpaceChat({
    bootstrapSpaceId: bootstrapMe?.spaceId ?? undefined,
    userId,
    navigate,
    navigationRevision,
    overlayRevision,
    closeMobileSidebar: () => setMobileSidebarOpen(false),
  });

  const activeSnapshot = inGroup
    ? snapshot?.groupId === groupId
      ? snapshot
      : null
    : snapshot?.botId === active?.id
      ? snapshot
      : null;
  const currentRuns = activeThreadRuns(activeSnapshot);
  const answerableAskIds = useMemo(() => answerableAskMessageIds(activeSnapshot), [activeSnapshot]);
  const dismissableAskIds = useMemo(
    () => dismissableAskMessageIds(activeSnapshot, userId),
    [activeSnapshot, userId],
  );
  const workingRuns = currentRuns.filter((run) => isRunWorking(run.status));
  const transcriptRunning = workingRuns.length > 0;
  const composerRunning = currentRuns.some((run) => isActive(run.status));
  const transcript = useMemo(
    () => foldPeerReplies(activeSnapshot?.messages ?? [], { includePeerReceipts: true }),
    [activeSnapshot?.messages],
  );
  const transcriptArtifactTarget = useMemo<ArtifactTarget>(
    () => (inGroup ? { groupId: groupId ?? "" } : { botId: active?.id ?? "" }),
    [active?.id, groupId, inGroup],
  );
  const transcriptMembers = activeSnapshot?.members ?? activeGroup?.members;
  const resolveTranscriptBot = useCallback(
    (botId: string) => {
      const bot = bots.find((candidate) => candidate.id === botId);
      if (bot) return bot;
      return transcriptMembers?.find((member) => member.botId === botId);
    },
    [bots, transcriptMembers],
  );
  const workingBots: GroupAvatarMember[] = workingRuns.map((run) => {
    const bot = resolveTranscriptBot(run.botId);
    return {
      botId: run.botId,
      color: bot?.color ?? FALLBACK_BOT_COLOR,
      avatarSet: bot?.avatarSet,
      name: bot?.name,
      status: run.status,
    };
  });
  const resolveTranscriptMemberName = useCallback(
    (botId: string | undefined) => memberName(transcriptMembers, botId),
    [transcriptMembers],
  );
  const { composerMentionTargets } = useComposerMentions({
    rpc,
    queries,
    bots,
    groups,
    inGroup,
    groupId,
    integrationsOpen: overlay?.kind === "integrations",
    initialBotsLoaded,
  });
  const shellReady =
    initialBotsLoaded &&
    (inGroup
      ? Boolean(activeGroup && activeSnapshot)
      : bots.length === 0 || Boolean(active && activeSnapshot));

  useLayoutEffect(() => {
    if (initialBotsLoaded) {
      markOnce("rk:renderer:bots-committed");
      markAfterPaint("rk:renderer:bots-painted");
    }
    if (active && snapshot?.botId === active.id) {
      markOnce("rk:renderer:thread-committed");
      markAfterPaint("rk:renderer:thread-painted");
    }
    if (shellReady) {
      markShellReady();
      markOnce("rk:renderer:shell-ready");
      markAfterPaint("rk:renderer:shell-painted");
    }
  }, [active, initialBotsLoaded, shellReady, snapshot?.botId]);

  const { scrollRequest, clearScrollRequest, jumpToReplyMessage } = useMessageJump({
    rpc,
    queries,
    thread,
    commitComputer,
    transcript,
    active,
    inGroup,
    groupId,
    activeBotId,
    activeGroupId,
    searchParams,
    setSearchParams,
    setPanel,
    setPanelState: rightPanel.setPanelState,
    editRoutine,
    resetRoutineTarget,
  });
  const computerControls = useComputerControls({
    rpc,
    state: computerState,
    panel,
    active,
    bots,
    botsRef,
    activeBotId,
    activeGroupId,
    runStatus: snapshot?.run?.status,
    sandboxProvider: bootstrapMe?.sandboxProvider,
    refreshThread,
    refreshGroupThread: (id) => refreshGroupThreadRef.current(id),
  });
  const { computerBot, openComputer } = computerControls;
  const composer = useComposerActions({
    rpc,
    activeId: active?.id,
    inGroup,
    groupId,
    activeBotId,
    activeGroupId,
    botsRef,
    bootstrapMe,
    activeSnapshot,
    navigate,
    refreshBots,
    refreshThreadRef,
    refreshGroupThreadRef,
    updateSnapshot: thread.updateSnapshot,
    terminalRunReceipts,
    computerRef: computerState.computerRef,
    commitComputer,
    clearReadBoundary: read.clearReadBoundary,
    flushPendingBrowserNotifications,
  });
  const { sending, sendError, stopRun } = composer;
  function openChatSettings(kind: "bot" | "group", id: string) {
    const destKey =
      userId && bootstrapMe?.spaceId
        ? rightPanelStorageKey(userId, bootstrapMe.spaceId, kind, id)
        : null;
    rightPanel.openPanelInChat(destKey, kind === "bot" ? "settings" : "group-settings", () =>
      navigate(kind === "bot" ? `/app/${id}` : `/app/g/${id}`),
    );
  }

  const replyTargetName = composer.replyTarget
    ? composer.replyTarget.role === "user"
      ? (channelMessageSender(composer.replyTarget.blocks) ?? t`You`)
      : (resolveTranscriptMemberName(composer.replyTarget.botId) ?? active?.name ?? t`Bot`)
    : undefined;
  useRightPanelRestore({
    rightPanel,
    userId,
    spaceId: bootstrapMe?.spaceId ?? undefined,
    inGroup,
    panelTarget: inGroup ? activeGroup?.id : active?.id,
    activeId: active?.id,
    routines: thread.routines,
    routinesBotId: thread.routinesBotId,
    searchParams,
    editingRoutineId: editingRoutine?.id,
    restoreRoutine,
    leaveRoutine,
    enabled: !isMobileSidebar,
  });
  const rosterDrop = useRosterDrop({
    rpc,
    bots,
    reorderRosterBot: roster.reorderRosterBot,
    refreshBots,
    refreshGroupThread: (id) => refreshGroupThreadRef.current(id),
    activeGroupId,
    setSendError: composer.setSendError,
  });

  const openBot = useCallback((id: string) => navigate(`/app/${id}`), [navigate]);
  const setOverlayRef = useRef(setOverlay);
  useLayoutEffect(() => {
    setOverlayRef.current = setOverlay;
  }, [setOverlay]);
  const replyToMessage = useCallback(
    (message: ThreadMessage) => {
      composer.setReplyTarget(message);
      composer.setReplyQuote(null);
    },
    [composer.setReplyQuote, composer.setReplyTarget],
  );
  const quoteMessage = useCallback(
    (message: ThreadMessage, quote: string) => {
      composer.setReplyTarget(message);
      composer.setReplyQuote(quote);
    },
    [composer.setReplyQuote, composer.setReplyTarget],
  );
  const markMessageUnread = useCallback(
    (message: ThreadMessage) => {
      if (groupId) read.markGroupUnreadFrom(groupId, message.id);
      else if (active?.id) void read.markBotUnread(active.id, message.id).catch(() => undefined);
    },
    [active?.id, groupId, read.markBotUnread, read.markGroupUnreadFrom],
  );
  const openPeerMessages = useCallback(
    (peer: { peerBotId: string; peerBotName: string }) => {
      setPeerConversation(peer);
    },
    [setPeerConversation],
  );
  const stopTeaching = useCallback(async () => {
    const id = activeBotId.current;
    if (!id || teachBusy) return;
    const recording = thread.taughtSkills.find(
      (skill) => skill.status === "recording" && thread.taughtSkillsBotId === id,
    );
    if (!recording) return;
    setTeachBusy(true);
    try {
      await rpc.skills.stop({ skillId: recording.id });
      await refreshThreadRef.current(id);
      setComputerOpen(false);
    } finally {
      setTeachBusy(false);
    }
  }, [teachBusy, thread.taughtSkills, thread.taughtSkillsBotId]);
  // Teach chrome needs skills applied before this resolves — refreshThread only
  // kicks skills.list off in the background, so Stop teaching would never mount
  // if that background call failed or lagged behind local recovery.
  const refreshActiveTeaching = useCallback(async () => {
    const id = activeBotId.current;
    if (!id) return;
    await refreshThreadRef.current(id);
    const skills = await fetchFresh(
      queryClient,
      queries.skills.list.queryOptions({ input: { botId: id } }),
    );
    if (activeBotId.current !== id) return;
    thread.setTaughtSkills(skills);
    thread.setTaughtSkillsBotId(id);
  }, []);

  function setBotsSidebarCollapsedPref(collapsed: boolean) {
    setBotsSidebarCollapsed(collapsed);
    writeBotsSidebarCollapsed(userId, collapsed);
  }

  function openSettings(section: SettingsSection = "general", spaceId: string | null = null) {
    setOverlay({ kind: "settings", section, spaceId });
  }

  // Space-scoped sections bind their own rpc client to this space, so another
  // space's settings open in place without switching the app or reloading.
  function openSpaceSettings(spaceId: string) {
    openSettings("space", spaceId);
  }

  const settingsNavItems = useSettingsNavItems({
    messaging: messagingSurfaceEnabled,
    wallet: Boolean(bootstrapMe?.paymentWalletProvider),
    computer: computersAreUnavailable(bootstrapMe?.sandboxProvider),
    billing: bootstrapMe?.billingEnabled === true,
    updates: Boolean(desktopBridge()?.update),
  });
  const paletteActions: CommandPaletteAction[] = [
    {
      id: "new-team",
      label: t`New company`,
      icon: Users,
      onSelect: () => dialogs.setCreateTeamOpen(true),
    },
    {
      id: "integrations",
      label: t`Connect apps`,
      icon: Plug,
      onSelect: () => setOverlay({ kind: "integrations" }),
    },
    {
      id: "skills",
      label: t`Skills`,
      icon: ScrollText,
      onSelect: () => setOverlay({ kind: "skills" }),
    },
    ...settingsNavItems.map(({ id, label, icon }) => ({
      id: `settings-${id}`,
      label: t`Settings: ${label}`,
      icon,
      onSelect: () => openSettings(id),
    })),
    ...(
      [
        { value: "system", label: t`System`, icon: Monitor },
        { value: "light", label: t`Light`, icon: Sun },
        { value: "dark", label: t`Dark`, icon: Moon },
      ] satisfies { value: AppearancePreference; label: string; icon: typeof Sun }[]
    ).map(({ value, label, icon }) => ({
      id: `appearance-${value}`,
      label: t`Appearance: ${label}`,
      icon,
      checked: getUiAppearancePreference() === value,
      onSelect: () => setUiAppearance(value),
    })),
  ];

  async function renameSpace(spaceId: string, name: string) {
    await commitSpaceRename({
      rename: async () => {
        await rpc.spaces.rename({ spaceId, name });
      },
      apply: () => {
        setSpaces((current) =>
          current.map((space) => (space.id === spaceId ? { ...space, name } : space)),
        );
      },
      refresh: () => refreshBots(),
    });
  }

  async function finishSpaceRemoval(spaceId: string, activeSpaceId: string | null) {
    if (!alive.current) return;
    setPanel(null);
    if ((selectedSpaceId() ?? bootstrapMe?.spaceId) === spaceId) {
      // The last company leaves no Space to switch to; the welcome page creates one.
      if (!activeSpaceId) {
        clearSpaceSelection();
        navigate("/onboarding", { replace: true });
        return;
      }
      // Never refresh through the RPC client bound to the deleted Space.
      if (!selectSpace(activeSpaceId)) throw new Error(t`Could not switch spaces`);
      window.location.assign("/app");
      return;
    }
    await refreshBots(true);
  }

  async function removeSpace(spaceId: string) {
    const company = spaces.find((space) => space.id === spaceId)?.companyPlatform;
    const result = company
      ? await rpc.companies.remove({ spaceId })
      : await rpc.spaces.remove({ spaceId });
    await finishSpaceRemoval(spaceId, result.activeSpaceId);
  }

  async function removeGroup(group: Group) {
    if (group.isCompany === undefined) throw new Error(t`Could not delete group`);
    if (group.isCompany) {
      const result = await rpc.companies.remove({ spaceId: group.spaceId });
      await finishSpaceRemoval(group.spaceId, result.activeSpaceId);
      return;
    }
    await rpc.groups.remove({ groupId: group.id });
    if (!alive.current) return;
    const remainingGroups = groups.filter((item) => item.id !== group.id);
    setGroups(remainingGroups);
    setPanel(null);
    if (group.id === groupId) {
      navigate(firstThreadRoute(bots, remainingGroups), { replace: true });
    }
    await refreshBots(true);
  }

  async function createTeam(template: TeamTemplate, name: string, idempotencyKey: string) {
    const { spaceId, groupId } = await rpc.companies.create({
      name,
      platform: template.id,
      idempotencyKey,
    });
    dialogs.setCreateTeamOpen(false);
    setPanel(null);
    if (noCompany) {
      if (!selectSpace(spaceId)) throw new Error(t`Could not switch spaces`);
      window.location.assign(`/app/g/${groupId}`);
      return;
    }
    openSpaceChat(spaceId, `/app/g/${groupId}`);
  }

  const contextBot =
    botMenu?.kind === "bot" ? bots.find((bot) => bot.id === botMenu.id) : undefined;
  const contextGroup =
    botMenu?.kind === "group" ? groups.find((group) => group.id === botMenu.id) : undefined;
  const contextCompany = contextGroup?.isCompany === true;
  const botMenuContent =
    contextBot || contextGroup ? (
      <ChatContextMenu
        rpc={rpc}
        bot={contextBot}
        group={contextGroup}
        company={contextCompany}
        sections={botSections}
        spaceId={bootstrapMe?.spaceId ?? undefined}
        onClose={closeBotMenu}
        refreshBots={refreshBots}
        read={read}
        dialogs={dialogs}
        openChatSettings={openChatSettings}
        navigate={navigate}
      />
    ) : null;
  const shell = (
    <BotsSidebarLayout
      userId={userId}
      collapsed={botsSidebarCollapsed}
      onCollapsedChange={setBotsSidebarCollapsedPref}
      onPreviewCollapsedChange={setBotsSidebarCollapsed}
      computerOpen={computerOpen}
      data-testid="shell-root"
      data-ready={shellReady}
      className="relative flex h-full min-h-0 min-w-0 overflow-hidden bg-background text-foreground/90 [&[data-resizing-sidebar]_*[data-slot=sidebar-gap]]:transition-none [&[data-resizing-sidebar]_*[data-slot=sidebar-container]]:transition-none"
      {...swipeHandlers}
    >
      <ComputerUpdateProgress
        onCompleted={() => {
          if (active) void refreshThread(active.id);
        }}
      />
      {bootstrapMe !== undefined ? (
        <HostComputerPrompt initialMe={bootstrapMe ?? undefined} onMeUpdated={setBootstrapMe} />
      ) : null}
      <ShellSidebar
        isMobile={isMobileSidebar}
        mobileOpen={mobileSidebarOpen}
        setMobileOpen={setMobileSidebarOpen}
        density={botsSidebarDensity}
        collapsed={botsSidebarCollapsed}
        mini={miniSidebar}
        setCollapsedPref={setBotsSidebarCollapsedPref}
        search={search}
        navigate={navigate}
        setCreateTeamOpen={dialogs.setCreateTeamOpen}
        roster={
          <SidebarRoster
            mini={miniSidebar}
            sidebarGroups={sidebarGroups}
            collapsedSidebarSections={collapsedSidebarSections}
            activeSpaceId={currentSpaceId}
            botSections={botSections}
            spaces={spaces}
            rosterDropKey={rosterDrop.rosterDropKey}
            setRosterDropKey={rosterDrop.setRosterDropKey}
            rosterDropAction={rosterDrop.rosterDropAction}
            applyRosterDrop={rosterDrop.applyRosterDrop}
            openSpaceChat={openSpaceChat}
            spaceMenuAnchor={dialogs.spaceMenuAnchor}
            setSpaceMenu={dialogs.setSpaceMenu}
            setRenameSectionTarget={dialogs.setRenameSectionTarget}
            setDeleteSectionTarget={dialogs.setDeleteSectionTarget}
            toggleSidebarSection={toggleSidebarSection}
            inGroup={inGroup}
            active={active}
            activeGroup={activeGroup}
            botsSidebarDensity={botsSidebarDensity}
            dismissedRunErrorIds={composer.dismissedRunErrorIds}
            displayedRunError={composer.displayedRunError}
            draggedBotId={rosterDrop.draggedBotId}
            setDraggedBotId={rosterDrop.setDraggedBotId}
            botMenu={botMenu}
            setBotMenu={setBotMenu}
            reorderRosterBot={roster.reorderRosterBot}
            activeSnapshot={activeSnapshot}
            teamDms={teamDms}
            botMenuContent={botMenuContent}
          />
        }
        archived={
          <ArchivedChats
            archivedBots={archivedBots}
            archivedGroups={archivedGroups}
            showSpaceSearch={search.showSpaceSearch}
            rpc={rpc}
            refreshBots={refreshBots}
            setDeleteTarget={dialogs.setDeleteTarget}
            setDeleteGroupTarget={dialogs.setDeleteGroupTarget}
          />
        }
        user={session.data?.user}
        ticketBoardEnabled={Boolean(bootstrapMe?.ticketBoardEnabled)}
        navLinks={desktopBridge() ? [] : (bootstrapMe?.navLinks ?? [])}
        openSettings={openSettings}
        openSkills={() => setOverlay({ kind: "skills" })}
      />

      {/* Keep the composer's z-index inside the chat so phone overlays like the side panel cover it. */}
      <main
        data-space-switch="main"
        aria-hidden={mobileSidebarOpen || undefined}
        inert={mobileSidebarOpen}
        className="isolate flex min-w-0 flex-1 flex-col overflow-hidden bg-background md:pe-1.5 md:pb-1.5"
      >
        <div aria-hidden="true" className="app-drag hidden h-1.5 shrink-0 md:block" />
        <div className="relative isolate flex min-h-0 flex-1 flex-col overflow-hidden bg-panel md:rounded-md md:border md:border-border">
          {/* Inside main so the composer and its menus stay above it. */}
          {!mobileSidebarOpen ? (
            <div
              data-testid="mobile-sidebar-swipe-edge"
              aria-hidden="true"
              className="absolute bottom-20 start-0 top-16 z-20 w-8 touch-none md:hidden"
            />
          ) : null}
          <CallReturnBar />
          {/* The header floats so the transcript scrolls under its buttons. */}
          <div className="relative flex min-h-0 flex-1 flex-col">
            {/* Only the pills take input; the gaps pass clicks and scrolling to the transcript. */}
            <div aria-hidden="true" className="app-drag absolute inset-x-0 top-0 z-10 h-3" />
            <ChatHeader
              inGroup={inGroup}
              active={active}
              activeGroup={activeGroup}
              activeSnapshot={activeSnapshot}
              leader={activeGroupLeader}
              phoneCallsEnabled={Boolean(bootstrapMe?.phoneCallsEnabled)}
              inCall={Boolean(active) && call?.botId === active?.id}
              panelOpen={chatPanelOpen || panelPermanent}
              panelPermanent={panelPermanent}
              windowControlsEnabled={!panelPermanent}
              onOpenNavigation={() => setMobileSidebarOpen(true)}
              onTogglePanel={() => {
                if (chatPanelOpen) setPanel(null);
                else if (inGroup) setPanel("group-settings");
                else if (active) {
                  setPanel(lastBotTab.current);
                  // Refresh run/computer so Take control isn't stuck on a stale busyBotName.
                  void refreshThread(active.id).catch(() => undefined);
                }
              }}
              onOpenLeader={(bot) => {
                navigate(`/app/${bot.id}`);
                setPanel(null);
              }}
              onCall={(bot) => {
                if (call?.botId === bot.id) {
                  setCallMinimized(false);
                  return;
                }
                callBot(bot);
              }}
            />
            {!active && !activeGroup && initialBotsLoaded ? (
              <div className="grid flex-1 place-items-center">
                <div className="flex flex-col items-center gap-4">
                  <BrandMark className="size-12" />
                  <h1 className="text-heading3 font-semibold text-foreground">
                    <Trans>Welcome to ClVaBot</Trans>
                  </h1>
                  <Button onClick={() => dialogs.setCreateTeamOpen(true)}>
                    <Users size={16} aria-hidden="true" />
                    <Trans>New company</Trans>
                  </Button>
                </div>
              </div>
            ) : (
              <Transcript
                key={activeSnapshot?.threadId}
                scrollRef={thread.messageScroll}
                scrollRequest={scrollRequest}
                onScrollRequestHandled={clearScrollRequest}
                followRequest={composer.sentCount}
                artifactTarget={transcriptArtifactTarget}
                markedUnreadMessageId={read.markedUnreadMessageId}
                transcript={transcript}
                showToolActivity={showToolActivity}
                olderCursor={activeSnapshot?.olderCursor ?? null}
                unreadAfterSeq={read.unreadAfterSeq}
                loadingOlder={loadingOlder}
                answerableAskIds={answerableAskIds}
                dismissableAskIds={dismissableAskIds}
                running={transcriptRunning}
                workingBots={workingBots}
                userBubble={userBubble}
                onCallBack={
                  bootstrapMe?.phoneCallsEnabled && !inGroup && active
                    ? () => callBot(active)
                    : undefined
                }
                onLoadOlder={loadOlder}
                onOpenBot={openBot}
                onAnswer={composer.answerMessage}
                onReply={replyToMessage}
                onMarkUnread={markMessageUnread}
                onQuote={quoteMessage}
                onReact={composer.reactToMessage}
                onJumpToMessage={jumpToReplyMessage}
                onOpenPeerMessages={openPeerMessages}
                memberName={resolveTranscriptMemberName}
                peerBot={resolveTranscriptBot}
                onRefresh={refreshActiveThread}
                onBotChanged={refreshBots}
                onAddRoutine={addSkillRoutine}
                voiceReady={Boolean(voiceStatus?.ready)}
                speakingMessageId={speakingMessageId}
                onSpeak={speakMessage}
                onOpenComputer={computerControls.onOpenComputer}
              />
            )}
          </div>
          {active || activeGroup ? (
            // Let messages continue behind the composer's rounded top corners.
            <div className="pointer-events-none relative z-30 -mt-7">
              {recordingSkill ? (
                <div className="pointer-events-auto bg-background px-6 pb-2 text-center text-[13px] text-destructive">
                  <Trans>Teaching in progress. Stop teaching before sending a new message.</Trans>
                </div>
              ) : null}
              <Composer
                key={inGroup ? `group:${groupId}` : `bot:${active?.id}`}
                draftKey={inGroup ? `group:${groupId}` : `bot:${active?.id}`}
                enterToSend={enterToSend}
                activeName={
                  inGroup ? (activeGroup?.name ?? activeSnapshot?.groupName) : active?.name
                }
                running={composerRunning}
                disabled={Boolean(recordingSkill)}
                pendingAttachments={composer.activePendingAttachments}
                attachmentNotice={composer.attachmentNotice}
                sendError={sendError}
                imageConfirmation={composer.imageConfirmation}
                onDraftEdited={composer.clearImageConfirmation}
                onOpenModels={() => {
                  // A group's failed run belongs to one member; change that bot's model.
                  const failedBotId =
                    inGroup && composer.displayedRunError ? activeSnapshot?.run?.botId : null;
                  if (active && !inGroup) {
                    setPanel("settings");
                    setModelFocusRequest(Date.now());
                  } else if (failedBotId) {
                    openChatSettings("bot", failedBotId);
                    setModelFocusRequest(Date.now());
                  } else openSettings("models");
                }}
                onOpenConnections={() => openSettings("models")}
                runError={composer.displayedRunError}
                runErrorId={composer.displayedRunErrorId}
                onRunErrorPresented={composer.handleRunErrorPresented}
                onDismissError={composer.dismissComposerError}
                sending={sending}
                fileInputRef={composer.fileInputRef}
                onAttachmentPick={composer.onAttachmentPick}
                onRemoveAttachment={composer.removeAttachment}
                onSend={composer.sendMessage}
                onStop={stopRun}
                phoneCallsEnabled={bootstrapMe?.phoneCallsEnabled === true}
                dictationTranscribe={voiceStatus ? Boolean(voiceStatus.transcribe) : undefined}
                artifactTarget={transcriptArtifactTarget}
                replyTarget={composer.replyTarget}
                replyQuote={composer.replyQuote}
                replyTargetName={replyTargetName}
                onClearReply={composer.clearReply}
                mentionTargets={composerMentionTargets}
                agentSkills={agentSkills}
                onSlashOpen={refreshAgentSkills}
                onSlashAction={(action) => {
                  if (action === "chat-settings") {
                    setPanel(inGroup ? "group-settings" : "settings");
                    return;
                  }
                  if (action === "settings-general") {
                    openSettings("general");
                    return;
                  }
                  if (action === "settings-usage") {
                    openSettings("usage");
                  }
                }}
              />
            </div>
          ) : null}
        </div>
      </main>

      <CallCard onSettings={() => openSettings("voice")} />
      <IncomingCall
        bots={bootstrapMe?.phoneCallsEnabled ? bots : []}
        transcribe={voiceStatus ? Boolean(voiceStatus.transcribe) : undefined}
        onAnswered={(botId) => navigate(`/app/${botId}`)}
      />

      <ShellSidePanel
        rpc={rpc}
        panel={panel}
        setPanel={setPanel}
        permanent={!isMobileSidebar}
        active={active}
        activeGroup={activeGroup}
        company={Boolean(
          spaces.find((space) => space.id === activeGroup?.spaceId)?.companyPlatform,
        )}
        bots={bots}
        setBots={setBots}
        setGroups={setGroups}
        removeGroup={removeGroup}
        refreshBots={refreshBots}
        refreshThread={refreshThread}
        refreshGroupThread={refreshGroupThread}
        computer={computer}
        computerOpen={computerOpen}
        booting={booting}
        computerPanel={
          active ? (
            <ComputerPanel
              bot={active}
              computer={computer}
              computerOpen={computerOpen}
              embeddedScreenUrl={computerControls.embeddedScreenUrl}
              screenFrameKey={computerState.screenFrameKey}
              screenFrameRef={computerState.setScreenFrame}
              computerScreenError={computerControls.computerScreenError}
              computerBooting={computerControls.computerBooting}
              booting={booting}
              showingRecoveryHint={computerControls.showingRecoveryHint}
              showComputerRecoveryHint={computerControls.showComputerRecoveryHint}
              sandboxProvider={bootstrapMe?.sandboxProvider}
              onRecovered={(sandboxProvider) => {
                setBootstrapMe((prev) => (prev ? { ...prev, sandboxProvider } : prev));
                computerControls.holdComputerRecovery();
              }}
              onRecoveryDismissed={computerControls.releaseComputerRecovery}
              onOpenComputerSettings={() => openSettings("computer")}
              onOpen={() => void openComputer()}
            />
          ) : null
        }
        routinesPanel={
          <RoutineList
            routines={activeRoutines}
            runningRoutineId={
              snapshot?.run && isActive(snapshot.run.status)
                ? (snapshot.run.routineId ?? undefined)
                : undefined
            }
            onCreate={() => startRoutine()}
            onEdit={startRoutine}
            onStop={() => void stopRun()}
          />
        }
        routineEditorProps={routineEditorProps}
        openBot={openBot}
        modelFocusRequest={modelFocusRequest}
        openSettings={openSettings}
        memoryProviderConfigured={memoryProviderConfig != null}
        setAgentSkills={setAgentSkills}
        onClearBot={(bot) => dialogs.setClearTarget({ kind: "bot", chat: bot })}
      />

      <Suspense fallback={null}>
        <ShellDialogs
          dialogs={dialogs}
          rpc={rpc}
          spaces={spaces}
          bots={bots}
          activeId={active?.id}
          activeGroupId={activeGroup?.id}
          navigate={navigate}
          setPanel={setPanel}
          refreshBots={refreshBots}
          removeSpace={removeSpace}
          removeGroup={removeGroup}
          openSpaceSettings={openSpaceSettings}
          closeMobileSidebar={() => setMobileSidebarOpen(false)}
          createTeam={createTeam}
          clearOpenThread={() => {
            thread.resetHistory();
            thread.updateSnapshot((current) =>
              current ? { ...current, messages: [], olderCursor: null, run: null } : current,
            );
          }}
          deleteRoutineDialogProps={deleteRoutineDialogProps}
          paletteActions={paletteActions}
        />
        <AppOverlays
          overlay={overlay}
          setOverlay={setOverlay}
          activeBotId={activeBotId.current}
          stdioEnabled={bootstrapMe?.mcpStdioEnabled === true}
        />
      </Suspense>

      <Suspense fallback={null}>
        <SettingsOverlays
          rpc={rpc}
          queries={queries}
          overlay={overlay}
          setOverlay={setOverlay}
          settingsSpace={settingsSpace}
          showSettings={settingsSpaceId === null || Boolean(settingsSpace)}
          spaces={spaces}
          renameSpace={renameSpace}
          removeSpace={removeSpace}
          refreshSpaces={() => refreshBots()}
          alive={alive}
          user={session.data?.user}
          refetchSession={() => session.refetch({ query: { disableCookieCache: true } })}
          bootstrapMe={bootstrapMe}
          setBootstrapMe={setBootstrapMe}
          messagingEnabled={messagingSurfaceEnabled}
          memoryConfig={memoryProviderConfig}
          peerConversation={peerConversation}
          setPeerConversation={setPeerConversation}
          active={active}
          peerBot={resolveTranscriptBot}
        />
      </Suspense>

      {computerOpen && computerBot ? (
        <ComputerOverlay
          bot={computerBot}
          computer={computer}
          computerViewport={computerState.computerViewport}
          recordingSkill={recordingSkill}
          teachBusy={teachBusy}
          hasControl={computerControls.hasControl}
          composerRunning={composerRunning}
          sending={sending}
          booting={booting}
          runStatus={snapshot?.run?.status}
          sendError={sendError}
          embeddedScreenUrl={computerControls.embeddedScreenUrl}
          screenFrameKey={computerState.screenFrameKey}
          screenFrameRef={computerState.setScreenFrame}
          computerScreenError={computerControls.computerScreenError}
          computerBooting={computerControls.computerBooting}
          onStopTeaching={stopTeaching}
          onStopRun={() => void stopRun()}
          onRelease={computerControls.releaseComputer}
          onTakeControl={() => void openComputer(computerBot.id, true)}
          onStart={() => void openComputer(computerBot.id)}
          onRefreshTeaching={refreshActiveTeaching}
          onRefreshComputer={async () => {
            await computerControls.refreshComputerFor(computerBot.id);
          }}
          onClose={() => setComputerOpen(false)}
        />
      ) : null}
    </BotsSidebarLayout>
  );

  // Empty chrome until the first bot list lands would flash "Select a bot" before the chat.
  return initialBotsLoaded || initialLoadFailed ? shell : <PageLoading />;
}
